import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Wallet, TrendingUp, FileText, ArrowDownLeft, ArrowUpRight, Receipt, PieChart, Plus, Trash2, X, Landmark, ScrollText, BarChart3, ListOrdered, Stethoscope, UserCircle2, Building2, CreditCard } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Card, CardHead, Btn, Badge, Spinner, Avatar } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { PortalPWA } from "@/components/PortalPWA";
import RecordDetailModal from "@/components/RecordDetailModal";
import api, { docFileUrl } from "@/lib/api";

const TABS = [
  { k: "overview", label: "Overview", icon: TrendingUp },
  { k: "transactions", label: "Transactions", icon: ListOrdered },
  { k: "invoices", label: "Invoicing", icon: FileText },
  { k: "clinician", label: "Clinician Billing", icon: Stethoscope },
  { k: "doctorshare", label: "Doctor Share", icon: UserCircle2 },
  { k: "platform", label: "PranIQ Profit", icon: Building2 },
  { k: "billing", label: "Manage Billing", icon: CreditCard },
  { k: "ar", label: "Receivables", icon: ArrowDownLeft },
  { k: "ap", label: "Payables", icon: ArrowUpRight },
  { k: "expenses", label: "Expenses", icon: Receipt },
  { k: "pnl", label: "P&L", icon: Wallet },
  { k: "profit", label: "Profitability", icon: PieChart },
  { k: "gst", label: "GST Master", icon: Landmark },
  { k: "notes", label: "Cr/Dr Notes", icon: ScrollText },
  { k: "reports", label: "GST Reports", icon: BarChart3 },
];
const money = (n, c = "₹") => `${c}${Number(n || 0).toLocaleString("en-IN")}`;
const invTone = (s) => (s === "Paid" ? "emerald" : s === "Overdue" ? "rose" : s === "Sent" ? "sky" : "slate");

const STATES = ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"];

const StateSelect = ({ value, onChange, testid, className = "pq-input py-1.5" }) => (
  <select className={className} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    <option value="">Select state</option>
    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
  </select>
);

const gstCalc = (items, taxMode, customerState, regState) => {
  const intra = !!regState && (customerState || "").trim().toLowerCase() === regState.trim().toLowerCase();
  let taxable = 0, cgst = 0, sgst = 0, igst = 0;
  items.forEach((it) => {
    const qty = Number(it.qty) || 1, rate = Number(it.rate) || 0, pct = Number(it.gst_rate) || 0;
    const gross = qty * rate;
    const t = taxMode === "inclusive" && pct ? Math.round((gross * 100) / (100 + pct)) : Math.round(gross);
    const tax = Math.round((t * pct) / 100);
    taxable += t;
    if (intra) { const h = Math.round(tax / 2); cgst += h; sgst += tax - h; } else igst += tax;
  });
  return { taxable, cgst, sgst, igst, tax: cgst + sgst + igst, total: taxable + cgst + sgst + igst, intra };
};

export default function Finance() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  return (
    <div className="min-h-screen bg-slate-50" data-testid="finance-portal">
      <header className="flex items-center justify-between px-6 py-3.5 border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="flex items-center gap-3"><Logo height={30} /><span className="text-[13px] font-semibold text-slate-500 border-l border-slate-200 pl-3">Finance</span></div>
        <div className="flex items-center gap-3"><span className="text-[13px] text-slate-600">{user?.name}</span>
          <Btn size="sm" onClick={() => { logout(); window.location.href = "/finance/login"; }} data-testid="finance-signout"><LogOut size={14} /> Sign out</Btn></div>
      </header>
      <div className="max-w-6xl mx-auto px-5 py-6">
        <PortalPWA portal="finance" />
        <h1 className="font-display text-2xl font-extrabold text-slate-900 mb-1">Finance &amp; Accounting</h1>
        <p className="text-[13px] text-slate-500 mb-5">Invoicing, receivables, payables, expenses, P&amp;L, profitability and GST compliance.</p>
        <div className="flex gap-2 flex-wrap mb-6">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`finance-tab-${t.k}`}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[13px] font-semibold transition-colors ${tab === t.k ? "bg-navy-700 text-white" : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
        {tab === "overview" && <Overview />}
        {tab === "transactions" && <Transactions />}
        {tab === "invoices" && <Invoices />}
        {tab === "clinician" && <ClinicianBilling />}
        {tab === "doctorshare" && <DoctorShares />}
        {tab === "platform" && <PlatformProfit />}
        {tab === "billing" && <ManageBilling />}
        {tab === "ar" && <Ledger endpoint="/finance/ar" title="Accounts Receivable" testid="ar" />}
        {tab === "ap" && <Ledger endpoint="/finance/ap" title="Accounts Payable" testid="ap" />}
        {tab === "expenses" && <Expenses />}
        {tab === "pnl" && <PnL />}
        {tab === "profit" && <Profitability />}
        {tab === "gst" && <GstMaster />}
        {tab === "notes" && <Notes />}
        {tab === "reports" && <GstReports />}
      </div>
    </div>
  );
}

function KPI({ label, value, tone = "text-slate-900", testid }) {
  return <Card className="p-4" data-testid={testid}><div className={`metric text-2xl font-extrabold ${tone}`}>{value}</div><div className="text-[12px] text-slate-500 mt-1">{label}</div></Card>;
}

const TXN_META = {
  order_revenue: { label: "Order revenue", tone: "teal" },
  invoice: { label: "Invoice", tone: "sky" },
  expense: { label: "Expense", tone: "rose" },
  credit_note: { label: "Credit note", tone: "amber" },
  debit_note: { label: "Debit note", tone: "emerald" },
  clinician_payout: { label: "Clinician payout", tone: "indigo" },
};

function Transactions() {
  const [d, setD] = useState(null);
  const [type, setType] = useState("");
  const [month, setMonth] = useState("");
  const load = useCallback(() => {
    const q = [type && `ttype=${type}`, month && `month=${month}`].filter(Boolean).join("&");
    api.get(`/finance/transactions${q ? `?${q}` : ""}`).then((r) => setD(r.data));
  }, [type, month]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  return (
    <Card data-testid="finance-transactions">
      <CardHead title="All Transactions" action={<Badge tone="slate">{d.summary.count} records</Badge>} />
      <div className="flex items-end gap-3 flex-wrap px-5 py-4 border-b border-slate-100">
        <label className="block"><span className="pq-label">Type</span>
          <select className="pq-input py-1.5" value={type} onChange={(e) => setType(e.target.value)} data-testid="txn-type">
            <option value="">All types</option>
            {Object.entries(TXN_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <label className="block"><span className="pq-label">Month</span><input type="month" className="pq-input py-1.5" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="txn-month" /></label>
        <div className="ml-auto flex gap-4 text-[12.5px]">
          <span className="text-emerald-600 font-semibold" data-testid="txn-in">In {m(d.summary.in)}</span>
          <span className="text-rose-600 font-semibold" data-testid="txn-out">Out {m(d.summary.out)}</span>
          <span className={`font-bold ${d.summary.net >= 0 ? "text-teal-700" : "text-rose-600"}`} data-testid="txn-net">Net {m(d.summary.net)}</span>
        </div>
      </div>
      <div className="overflow-x-auto"><table className="w-full">
        <thead><tr>{["Date", "Type", "Reference", "Party", "Description", "Status", "Amount"].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>
          {d.transactions.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">No transactions for this filter.</td></tr> :
            d.transactions.map((t, i) => (
              <tr key={`${t.type}-${t.id}-${i}`} className="border-b border-slate-100 last:border-0 text-[13px]" data-testid={`txn-row-${i}`}>
                <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{t.date}</td>
                <td className="px-4 py-2.5"><Badge tone={(TXN_META[t.type] || {}).tone || "slate"}>{(TXN_META[t.type] || {}).label || t.type}</Badge></td>
                <td className="px-4 py-2.5 font-semibold whitespace-nowrap">{t.ref}</td>
                <td className="px-4 py-2.5">{t.party}</td>
                <td className="px-4 py-2.5 text-slate-500 max-w-[220px] truncate" title={t.description}>{t.description}</td>
                <td className="px-4 py-2.5"><Badge tone={/paid|disbursed|debit/i.test(t.status) ? "emerald" : /unpaid|submitted|draft/i.test(t.status) ? "amber" : "slate"}>{t.status}</Badge></td>
                <td className={`px-4 py-2.5 metric font-semibold whitespace-nowrap ${t.direction === "in" ? "text-emerald-600" : "text-rose-600"}`}>{t.direction === "in" ? "+" : "−"}{m(t.amount)}</td>
              </tr>
            ))}
        </tbody>
      </table></div>
    </Card>
  );
}

function ClinicianBilling() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/finance/clinician-billing").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  const setStatus = async (inv, status) => {
    try { await api.put(`/finance/clinician-billing/${inv.id}`, { status }); toast.success(`Invoice ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const stTone = (s) => (s === "Paid" ? "emerald" : s === "Approved" ? "teal" : s === "Rejected" ? "rose" : "amber");
  return (
    <div data-testid="finance-clinician">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <KPI label="Awaiting approval" value={m(d.summary.submitted)} tone="text-amber-600" testid="cb-submitted" />
        <KPI label="Approved · to pay" value={m(d.summary.approved)} tone="text-teal-700" testid="cb-approved" />
        <KPI label="Paid out" value={m(d.summary.paid)} tone="text-emerald-600" testid="cb-paid" />
      </div>
      <Card>
        <CardHead title="Clinician / Vendor Payout Invoices" action={<Badge tone="slate">{d.summary.count}</Badge>} />
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{["Invoice", "Clinician", "Period", "Cases", "Amount", "Status", ""].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {d.invoices.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">No clinician payout invoices yet.</td></tr> :
              d.invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-100 last:border-0 text-[13px]" data-testid={`cb-row-${inv.id}`}>
                  <td className="px-4 py-2.5 font-semibold whitespace-nowrap">{inv.invoice_no}</td>
                  <td className="px-4 py-2.5">{inv.clinician_name}<div className="text-[10px] text-slate-400">{inv.clinician_prq}</div></td>
                  <td className="px-4 py-2.5 text-slate-500">{inv.period}</td>
                  <td className="px-4 py-2.5">{(inv.line_items || []).length}</td>
                  <td className="px-4 py-2.5 metric font-semibold">{m(inv.amount)}</td>
                  <td className="px-4 py-2.5"><Badge tone={stTone(inv.status)}>{inv.status}</Badge></td>
                  <td className="px-4 py-2.5"><div className="flex gap-1.5 justify-end flex-wrap">
                    {inv.status === "Submitted" && <>
                      <Btn size="sm" variant="primary" onClick={() => setStatus(inv, "Approved")} data-testid={`cb-approve-${inv.id}`}>Approve</Btn>
                      <Btn size="sm" onClick={() => setStatus(inv, "Rejected")} data-testid={`cb-reject-${inv.id}`}>Reject</Btn>
                    </>}
                    {inv.status === "Approved" && <Btn size="sm" variant="primary" onClick={() => setStatus(inv, "Paid")} data-testid={`cb-pay-${inv.id}`}>Mark paid</Btn>}
                  </div></td>
                </tr>
              ))}
          </tbody>
        </table></div>
      </Card>
    </div>
  );
}

function DoctorShares() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/finance/doctor-shares").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  return (
    <div data-testid="finance-doctorshare">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <KPI label="Total doctor share (expected)" value={m(d.totals.expected)} testid="ds-expected" />
        <KPI label="Disbursed" value={m(d.totals.disbursed)} tone="text-emerald-600" testid="ds-disbursed" />
        <KPI label="Pending" value={m(d.totals.pending)} tone="text-amber-600" testid="ds-pending" />
      </div>
      <Card>
        <CardHead title="Doctor Share Accounting (per referring doctor)" />
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{["Doctor", "Orders", "Expected", "Disbursed", "Pending"].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
          <tbody>
            {d.doctors.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">No doctor-share data yet.</td></tr> :
              d.doctors.map((r) => (
                <tr key={r.doctor_id} className="border-b border-slate-100 last:border-0 text-[13px]" data-testid={`ds-row-${r.doctor_id}`}>
                  <td className="px-4 py-2.5 font-semibold">{r.name}</td>
                  <td className="px-4 py-2.5">{r.orders}</td>
                  <td className="px-4 py-2.5 metric">{m(r.expected)}</td>
                  <td className="px-4 py-2.5 metric text-emerald-600">{m(r.disbursed)}</td>
                  <td className="px-4 py-2.5 metric text-amber-600">{m(r.pending)}</td>
                </tr>
              ))}
          </tbody>
        </table></div>
      </Card>
    </div>
  );
}

function PlatformProfit() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/finance/platform-profit?months=12").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  return (
    <div data-testid="finance-platform">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KPI label="Order revenue (paid)" value={m(d.totals.order_revenue)} testid="pp-revenue" />
        <KPI label="PranIQ platform share" value={m(d.totals.platform_share)} tone="text-teal-700" testid="pp-share" />
        <KPI label="Operating expenses" value={m(d.totals.opex)} testid="pp-opex" />
        <KPI label="PranIQ net profit" value={m(d.totals.net)} tone={d.totals.net >= 0 ? "text-emerald-600" : "text-rose-600"} testid="pp-net" />
      </div>
      <Card>
        <CardHead title="PranIQ Profit (monthly)" />
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{["Month", "Order revenue", "Platform share", "Clinician payout", "Doctor share", "Opex", "PranIQ net"].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {d.rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">No paid orders yet.</td></tr> :
              d.rows.map((r) => (
                <tr key={r.month} className="border-b border-slate-100 last:border-0 text-[13px] whitespace-nowrap" data-testid={`pp-row-${r.month}`}>
                  <td className="px-4 py-2.5 font-semibold">{new Date(r.month + "-01").toLocaleDateString("en-GB", { month: "short", year: "numeric" })}</td>
                  <td className="px-4 py-2.5 metric">{m(r.order_revenue)}</td>
                  <td className="px-4 py-2.5 metric text-teal-700">{m(r.platform_share)}</td>
                  <td className="px-4 py-2.5 metric text-slate-500">{m(r.physio_payout)}</td>
                  <td className="px-4 py-2.5 metric text-slate-500">{m(r.doctor_share)}</td>
                  <td className="px-4 py-2.5 metric text-slate-500">{m(r.opex)}</td>
                  <td className={`px-4 py-2.5 metric font-bold ${r.net >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{m(r.net)}</td>
                </tr>
              ))}
          </tbody>
        </table></div>
      </Card>
    </div>
  );
}

function ManageBilling() {
  const [d, setD] = useState(null);
  const [view, setView] = useState(null);
  const load = useCallback(() => api.get("/finance/orders").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  const act = async (id, action, label) => {
    try { await api.post(`/finance/orders/${id}/${action}`); toast.success(label); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const payTone = (s) => (s === "Paid" ? "emerald" : "amber");
  const outTone = (s) => (s === "Disbursed" ? "emerald" : "amber");
  return (
    <Card data-testid="finance-billing">
      <CardHead title="Manage Order Billing" action={<Badge tone="slate">{d.orders.length} orders</Badge>} />
      <div className="overflow-x-auto"><table className="w-full">
        <thead><tr>{["Order", "Patient", "Doctor", "Clinician", "Amount", "PranIQ", "Patient payment", "Payout", ""].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>
          {d.orders.length === 0 ? <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-slate-500">No orders yet.</td></tr> :
            d.orders.map((o) => (
              <tr key={o.id} onClick={() => setView(o)} className="border-b border-slate-100 last:border-0 text-[13px] hover:bg-slate-50 cursor-pointer" data-testid={`bo-row-${o.id}`}>
                <td className="px-4 py-2.5 font-semibold whitespace-nowrap">{o.ref_no}</td>
                <td className="px-4 py-2.5">{o.patient_name}</td>
                <td className="px-4 py-2.5 text-slate-600"><div className="flex items-center gap-1.5">{(o.doctor || o.doctor_photo) ? <Avatar name={o.doctor || "?"} size={22} src={docFileUrl(o.doctor_photo)} /> : null}<span>{o.doctor || "—"}</span></div></td>
                <td className="px-4 py-2.5 text-slate-600"><div className="flex items-center gap-1.5">{o.clinician ? <Avatar name={o.clinician} size={22} src={docFileUrl(o.clinician_photo)} /> : null}<span>{o.clinician || "—"}</span></div></td>
                <td className="px-4 py-2.5 metric font-semibold whitespace-nowrap">{m(o.amount)}</td>
                <td className="px-4 py-2.5 metric text-teal-700 whitespace-nowrap" title={`Clinician ${m(o.physio)} · Doctor ${m(o.doctor_share)}`}>{m(o.platform)}</td>
                <td className="px-4 py-2.5"><Badge tone={payTone(o.payment_status)}>{o.payment_status}</Badge></td>
                <td className="px-4 py-2.5"><Badge tone={outTone(o.payout_status)}>{o.payout_status}</Badge></td>
                <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}><div className="flex gap-1.5 justify-end flex-wrap">
                  {o.payment_status !== "Paid" && <Btn size="sm" variant="primary" onClick={() => act(o.id, "mark-paid", "Marked paid")} data-testid={`bo-pay-${o.id}`}>Mark paid</Btn>}
                  {o.payment_status === "Paid" && o.payout_status !== "Disbursed" && <Btn size="sm" onClick={() => act(o.id, "disburse", "Payout disbursed")} data-testid={`bo-disburse-${o.id}`}>Disburse</Btn>}
                </div></td>
              </tr>
            ))}
        </tbody>
      </table></div>
      {view && <RecordDetailModal
        title={view.ref_no}
        subtitle={`Order billing · ${view.patient_name}`}
        badge={<Badge tone={payTone(view.payment_status)}>{view.payment_status}</Badge>}
        onClose={() => setView(null)}
        sections={[
          { heading: "Parties", fields: [
            { label: "Patient", value: view.patient_name, testid: "bo-view-patient" },
            { label: "Doctor", value: view.doctor },
            { label: "Clinician", value: view.clinician },
          ] },
          { heading: "Money", fields: [
            { label: "Order amount", value: m(view.amount), testid: "bo-view-amount" },
            { label: "PranIQ platform", value: m(view.platform) },
            { label: "Clinician payout", value: m(view.physio) },
            { label: "Doctor share", value: m(view.doctor_share) },
            { label: "Payment status", value: view.payment_status },
            { label: "Payout status", value: view.payout_status },
          ] },
        ]} />}
    </Card>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/finance/summary").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  return (
    <div data-testid="finance-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KPI label="Revenue (collected)" value={m(d.revenue)} testid="kpi-revenue" />
        <KPI label="COGS (payouts)" value={m(d.cogs)} testid="kpi-cogs" />
        <KPI label="Gross profit" value={m(d.gross_profit)} tone="text-teal-700" testid="kpi-gross" />
        <KPI label={`Net profit · ${d.margin}%`} value={m(d.net_profit)} tone={d.net_profit >= 0 ? "text-emerald-600" : "text-rose-600"} testid="kpi-net" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI label="Operating expenses" value={m(d.opex)} testid="kpi-opex" />
        <KPI label="Accounts receivable" value={m(d.ar_total)} tone="text-amber-600" testid="kpi-ar" />
        <KPI label="Accounts payable" value={m(d.ap_total)} tone="text-rose-600" testid="kpi-ap" />
        <KPI label="Cash collected" value={m(d.collected)} tone="text-emerald-600" testid="kpi-collected" />
      </div>
    </div>
  );
}

function Ledger({ endpoint, title, testid }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(endpoint).then((r) => setD(r.data)), [endpoint]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  return (
    <Card data-testid={`finance-${testid}`}>
      <CardHead title={title} action={<Badge tone="slate">{m(d.total)} outstanding</Badge>} />
      <div className="grid grid-cols-4 gap-3 px-5 py-4">
        {["0-30", "31-60", "61-90", "90+"].map((b) => (
          <div key={b} className="bg-slate-50 rounded-xl p-3 text-center" data-testid={`${testid}-bucket-${b}`}>
            <div className="metric text-lg font-extrabold">{m(d.buckets[b])}</div><div className="text-[11px] text-slate-500 mt-0.5">{b} days</div>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto"><table className="w-full">
        <thead><tr>{["Source", "Reference", "Party", "Date", "Bucket", "Amount"].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
        <tbody>
          {d.items.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">Nothing outstanding.</td></tr> :
            d.items.map((it, i) => (
              <tr key={`${it.source}-${it.ref}-${i}`} className="border-b border-slate-100 last:border-0 text-[13px]">
                <td className="px-4 py-2.5">{it.source}</td><td className="px-4 py-2.5 font-semibold">{it.ref}</td>
                <td className="px-4 py-2.5">{it.party}</td><td className="px-4 py-2.5 text-slate-500">{it.date}</td>
                <td className="px-4 py-2.5"><Badge tone={it.bucket === "90+" ? "rose" : it.bucket === "0-30" ? "emerald" : "amber"}>{it.bucket}</Badge></td>
                <td className="px-4 py-2.5 metric font-semibold">{m(it.amount)}</td>
              </tr>
            ))}
        </tbody>
      </table></div>
    </Card>
  );
}

function PnL() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/finance/pnl?months=12").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  return (
    <Card data-testid="finance-pnl">
      <CardHead title="Profit & Loss (monthly)" />
      <div className="overflow-x-auto"><table className="w-full">
        <thead><tr>{["Month", "Revenue", "COGS", "Gross profit", "Opex", "Net profit"].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>
          {d.rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">No financial activity yet.</td></tr> :
            d.rows.map((r) => (
              <tr key={r.month} className="border-b border-slate-100 last:border-0 text-[13px] whitespace-nowrap" data-testid={`pnl-row-${r.month}`}>
                <td className="px-4 py-2.5 font-semibold">{new Date(r.month + "-01").toLocaleDateString("en-GB", { month: "short", year: "numeric" })}</td>
                <td className="px-4 py-2.5 metric">{m(r.revenue)}</td><td className="px-4 py-2.5 metric text-slate-500">{m(r.cogs)}</td>
                <td className="px-4 py-2.5 metric text-teal-700">{m(r.gross_profit)}</td><td className="px-4 py-2.5 metric text-slate-500">{m(r.opex)}</td>
                <td className={`px-4 py-2.5 metric font-bold ${r.net >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{m(r.net)}</td>
              </tr>
            ))}
        </tbody>
      </table></div>
    </Card>
  );
}

function Profitability() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/finance/profitability").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  const Tbl = ({ title, rows, testid }) => (
    <Card className="mb-5" data-testid={testid}>
      <CardHead title={title} />
      <div className="overflow-x-auto"><table className="w-full">
        <thead><tr>{["Name", "Orders", "Revenue", "Cost", "Profit", "Margin"].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">No data yet.</td></tr> :
            rows.map((r) => (
              <tr key={r.name} className="border-b border-slate-100 last:border-0 text-[13px]">
                <td className="px-4 py-2.5 font-semibold">{r.name}</td><td className="px-4 py-2.5">{r.orders}</td>
                <td className="px-4 py-2.5 metric">{m(r.revenue)}</td><td className="px-4 py-2.5 metric text-slate-500">{m(r.cost)}</td>
                <td className="px-4 py-2.5 metric text-teal-700">{m(r.profit)}</td>
                <td className="px-4 py-2.5"><Badge tone={r.margin >= 50 ? "emerald" : r.margin >= 25 ? "amber" : "rose"}>{r.margin}%</Badge></td>
              </tr>
            ))}
        </tbody>
      </table></div>
    </Card>
  );
  return <div data-testid="finance-profit"><Tbl title="Profitability by package" rows={d.packages} testid="profit-packages" /><Tbl title="Profitability by clinician" rows={d.clinicians} testid="profit-clinicians" /></div>;
}

function Expenses() {
  const [d, setD] = useState(null);
  const [gst, setGst] = useState(null);
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), category: "Rent", vendor: "", amount: "", status: "Paid", note: "", vendor_gstin: "", vendor_state: "", gst_rate: 0, itc_eligible: true });
  const load = useCallback(() => {
    api.get("/finance/expenses").then((r) => setD(r.data));
    api.get("/finance/gst").then((r) => setGst(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d || !gst) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  const activeRates = gst.rates.filter((r) => r.active);
  const amt = Number(f.amount) || 0;
  const pct = Number(f.gst_rate) || 0;
  const taxable = pct > 0 ? Math.round((amt * 100) / (100 + pct)) : amt;
  const gstAmt = amt - taxable;
  const intra = gst.settings.registered_state && f.vendor_state && f.vendor_state.toLowerCase() === gst.settings.registered_state.toLowerCase();
  const add = async () => {
    if (!(Number(f.amount) > 0)) return toast.error("Enter an amount greater than zero");
    try {
      await api.post("/finance/expenses", { ...f, amount: Number(f.amount), gst_rate: Number(f.gst_rate) || 0 });
      toast.success("Expense recorded"); setF({ ...f, vendor: "", amount: "", note: "", vendor_gstin: "", gst_rate: 0 }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this expense?")) return; await api.delete(`/finance/expenses/${id}`); toast.success("Deleted"); load(); };
  const toggle = async (e) => { await api.put(`/finance/expenses/${e.id}`, { status: e.status === "Paid" ? "Unpaid" : "Paid" }); load(); };
  const toggleItc = async (e) => { await api.put(`/finance/expenses/${e.id}`, { itc_eligible: !e.itc_eligible }); toast.success(e.itc_eligible ? "ITC disabled" : "ITC enabled"); load(); };
  return (
    <Card data-testid="finance-expenses">
      <CardHead title="Operating Expenses (vendor bills · input GST)" />
      <div className="px-5 py-4 border-b border-slate-100 space-y-2">
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
          <input type="date" className="pq-input py-1.5" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="exp-date" />
          <select className="pq-input py-1.5" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="exp-category">{d.categories.map((c) => <option key={c}>{c}</option>)}</select>
          <input className="pq-input py-1.5" placeholder="Vendor" value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} data-testid="exp-vendor" />
          <input type="number" className="pq-input py-1.5 metric" placeholder="Bill total (incl GST)" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="exp-amount" />
          <select className="pq-input py-1.5" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="exp-status">{["Paid", "Unpaid"].map((s) => <option key={s}>{s}</option>)}</select>
          <Btn variant="primary" onClick={add} data-testid="exp-add"><Plus size={14} /> Add</Btn>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-center">
          <input className="pq-input py-1.5" placeholder="Vendor GSTIN (optional)" value={f.vendor_gstin} onChange={(e) => setF({ ...f, vendor_gstin: e.target.value })} data-testid="exp-vendor-gstin" />
          <StateSelect value={f.vendor_state} onChange={(v) => setF({ ...f, vendor_state: v })} testid="exp-vendor-state" />
          <select className="pq-input py-1.5" value={f.gst_rate} onChange={(e) => setF({ ...f, gst_rate: e.target.value })} data-testid="exp-gst-rate">
            <option value={0}>No GST</option>
            {activeRates.filter((r) => r.rate > 0).map((r) => <option key={r.id} value={r.rate}>{r.name}</option>)}
          </select>
          <label className="flex items-center gap-2 text-[12px] text-slate-600"><input type="checkbox" checked={f.itc_eligible} onChange={(e) => setF({ ...f, itc_eligible: e.target.checked })} data-testid="exp-itc" /> ITC eligible</label>
          {pct > 0 && amt > 0 && (
            <span className="text-[11px] text-slate-500" data-testid="exp-gst-preview">Taxable {m(taxable)} · GST {m(gstAmt)} ({intra ? "CGST+SGST" : "IGST"})</span>
          )}
        </div>
      </div>
      <div className="overflow-x-auto"><table className="w-full">
        <thead><tr>{["Date", "Category", "Vendor", "Status", "Input GST", "Amount", ""].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
        <tbody>
          {d.expenses.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">No expenses recorded yet.</td></tr> :
            d.expenses.map((e) => (
              <tr key={e.id} className="border-b border-slate-100 last:border-0 text-[13px]" data-testid={`exp-row-${e.id}`}>
                <td className="px-4 py-2.5 text-slate-500">{e.date}</td><td className="px-4 py-2.5 font-semibold">{e.category}</td>
                <td className="px-4 py-2.5">{e.vendor || "—"}{e.vendor_gstin && <div className="text-[10px] text-slate-400">{e.vendor_gstin}</div>}</td>
                <td className="px-4 py-2.5"><button onClick={() => toggle(e)} data-testid={`exp-toggle-${e.id}`}><Badge tone={e.status === "Paid" ? "emerald" : "amber"}>{e.status}</Badge></button></td>
                <td className="px-4 py-2.5">
                  {(e.gst_amount || 0) > 0 ? (
                    <button onClick={() => toggleItc(e)} data-testid={`exp-itc-toggle-${e.id}`}>
                      <Badge tone={e.itc_eligible ? "emerald" : "slate"}>{m(e.gst_amount)} · {e.itc_eligible ? "ITC" : "no ITC"}</Badge>
                    </button>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-2.5 metric font-semibold">{m(e.amount)}</td>
                <td className="px-4 py-2.5 text-right"><button onClick={() => del(e.id)} className="text-rose-500 hover:text-rose-700" data-testid={`exp-del-${e.id}`}><Trash2 size={15} /></button></td>
              </tr>
            ))}
        </tbody>
      </table></div>
    </Card>
  );
}

function Invoices() {
  const [d, setD] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/finance/invoices").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  const setStatus = async (inv, status) => { await api.put(`/finance/invoices/${inv.id}`, { status }); toast.success(`Invoice ${status}`); load(); };
  const del = async (inv) => { if (!window.confirm(`Delete ${inv.invoice_no}?`)) return; await api.delete(`/finance/invoices/${inv.id}`); toast.success("Invoice deleted"); load(); };
  const gstLabel = (inv) => {
    if (!inv.tax) return "—";
    if ((inv.igst || 0) > 0) return `IGST ${m(inv.igst)}`;
    return `C ${m(inv.cgst || 0)} · S ${m(inv.sgst || 0)}`;
  };
  return (
    <Card data-testid="finance-invoices">
      <CardHead title="Customer Invoices (GST)" action={<Btn size="sm" variant="primary" onClick={() => setShowForm(true)} data-testid="inv-new"><Plus size={14} /> New invoice</Btn>} />
      <div className="overflow-x-auto"><table className="w-full">
        <thead><tr>{["Invoice", "Client", "Issued", "Due", "Taxable", "GST", "Total", "Status", ""].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
        <tbody>
          {d.invoices.length === 0 ? <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-slate-500">No invoices yet. Create your first invoice.</td></tr> :
            d.invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-slate-100 last:border-0 text-[13px]" data-testid={`inv-row-${inv.id}`}>
                <td className="px-4 py-2.5 font-semibold">{inv.invoice_no}</td>
                <td className="px-4 py-2.5">{inv.client_name}{inv.customer_gstin && <div className="text-[10px] text-slate-400">{inv.customer_gstin}</div>}</td>
                <td className="px-4 py-2.5 text-slate-500">{inv.issue_date}</td><td className="px-4 py-2.5 text-slate-500">{inv.due_date || "—"}</td>
                <td className="px-4 py-2.5 metric">{m(inv.subtotal)}</td>
                <td className="px-4 py-2.5 text-[11px] text-slate-600" title={gstLabel(inv)}>{m(inv.tax)}</td>
                <td className="px-4 py-2.5 metric font-semibold">{m(inv.total)}</td>
                <td className="px-4 py-2.5"><Badge tone={invTone(inv.status)}>{inv.status}</Badge></td>
                <td className="px-4 py-2.5"><div className="flex gap-1.5 justify-end flex-wrap">
                  {inv.status !== "Sent" && inv.status !== "Paid" && <Btn size="sm" onClick={() => setStatus(inv, "Sent")} data-testid={`inv-send-${inv.id}`}>Send</Btn>}
                  {inv.status !== "Paid" && <Btn size="sm" variant="primary" onClick={() => setStatus(inv, "Paid")} data-testid={`inv-paid-${inv.id}`}>Mark paid</Btn>}
                  {inv.status !== "Overdue" && inv.status !== "Paid" && <Btn size="sm" onClick={() => setStatus(inv, "Overdue")} data-testid={`inv-overdue-${inv.id}`}>Overdue</Btn>}
                  <button onClick={() => del(inv)} className="text-rose-500 hover:text-rose-700 px-1" data-testid={`inv-del-${inv.id}`}><Trash2 size={15} /></button>
                </div></td>
              </tr>
            ))}
        </tbody>
      </table></div>
      {showForm && <InvoiceForm currency={d.currency} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </Card>
  );
}

function InvoiceForm({ currency, onClose, onSaved }) {
  const [gst, setGst] = useState(null);
  const [client, setClient] = useState("");
  const [custGstin, setCustGstin] = useState("");
  const [custState, setCustState] = useState("");
  const [orderRef, setOrderRef] = useState("");
  const [due, setDue] = useState("");
  const [taxMode, setTaxMode] = useState("exclusive");
  const [items, setItems] = useState([{ desc: "", qty: 1, rate: "", hsn_sac: "", gst_rate: 0 }]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/finance/gst").then((r) => {
      setGst(r.data);
      setTaxMode(r.data.settings.default_tax_mode || "exclusive");
      const def = Number(r.data.settings.default_tax_rate) || 0;
      setItems((prev) => prev.map((it) => ({ ...it, gst_rate: it.gst_rate || def, hsn_sac: it.hsn_sac || r.data.settings.sac || "" })));
    }).catch(() => setGst({ settings: {}, rates: [] }));
  }, []);
  const setItem = (i, k, v) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const t = gstCalc(items, taxMode, custState, gst?.settings?.registered_state || "");
  const m = (n) => money(n, currency);
  const activeRates = (gst?.rates || []).filter((r) => r.active);
  const save = async () => {
    if (!client.trim()) return toast.error("Client name is required");
    if (!items.some((it) => Number(it.rate) > 0)) return toast.error("Add at least one line item with a rate");
    setSaving(true);
    try {
      await api.post("/finance/invoices", { client_name: client, order_ref: orderRef, due_date: due,
        customer_gstin: custGstin, customer_state: custState, tax_mode: taxMode,
        line_items: items.filter((it) => Number(it.rate) > 0).map((it) => ({ desc: it.desc, qty: Number(it.qty) || 1, rate: Number(it.rate) || 0, hsn_sac: it.hsn_sac, gst_rate: Number(it.gst_rate) || 0 })), status: "Draft" });
      toast.success("Invoice created"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create invoice"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="inv-form">
      <Card className="w-full max-w-3xl my-6">
        <CardHead title="New GST Invoice" action={<button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="inv-form-close"><X size={18} /></button>} />
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="pq-label">Client / patient name</span><input className="pq-input py-1.5" value={client} onChange={(e) => setClient(e.target.value)} data-testid="inv-client" /></label>
            <label className="block"><span className="pq-label">Order ref (optional)</span><input className="pq-input py-1.5" value={orderRef} onChange={(e) => setOrderRef(e.target.value)} data-testid="inv-orderref" /></label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="block"><span className="pq-label">Customer GSTIN (B2B)</span><input className="pq-input py-1.5" placeholder="Optional" value={custGstin} onChange={(e) => setCustGstin(e.target.value)} data-testid="inv-cust-gstin" /></label>
            <label className="block"><span className="pq-label">Customer state (place of supply)</span><StateSelect value={custState} onChange={setCustState} testid="inv-cust-state" className="pq-input py-1.5 w-full" /></label>
            <label className="block"><span className="pq-label">Tax treatment</span>
              <select className="pq-input py-1.5 w-full" value={taxMode} onChange={(e) => setTaxMode(e.target.value)} data-testid="inv-tax-mode">
                <option value="exclusive">Tax exclusive (GST added on top)</option>
                <option value="inclusive">Tax inclusive (rates include GST)</option>
              </select>
            </label>
          </div>
          <div className="space-y-2">
            <div className="pq-label">Line items</div>
            {items.map((it, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input className="pq-input py-1.5 flex-1" placeholder="Description" value={it.desc} onChange={(e) => setItem(i, "desc", e.target.value)} data-testid={`inv-item-desc-${i}`} />
                <input type="number" className="pq-input py-1.5 w-14 metric" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`inv-item-qty-${i}`} />
                <input type="number" className="pq-input py-1.5 w-24 metric" placeholder="Rate" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} data-testid={`inv-item-rate-${i}`} />
                <input className="pq-input py-1.5 w-24" placeholder="HSN/SAC" value={it.hsn_sac} onChange={(e) => setItem(i, "hsn_sac", e.target.value)} data-testid={`inv-item-hsn-${i}`} />
                <select className="pq-input py-1.5 w-24" value={it.gst_rate} onChange={(e) => setItem(i, "gst_rate", e.target.value)} data-testid={`inv-item-gst-${i}`}>
                  {activeRates.map((r) => <option key={r.id} value={r.rate}>{r.name}</option>)}
                  {!activeRates.some((r) => Number(r.rate) === Number(it.gst_rate)) && <option value={it.gst_rate}>{it.gst_rate}%</option>}
                </select>
                {items.length > 1 && <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-rose-500"><X size={15} /></button>}
              </div>
            ))}
            <Btn size="sm" onClick={() => setItems([...items, { desc: "", qty: 1, rate: "", hsn_sac: gst?.settings?.sac || "", gst_rate: Number(gst?.settings?.default_tax_rate) || 0 }])} data-testid="inv-add-item"><Plus size={13} /> Add line</Btn>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="pq-label">Due date</span><input type="date" className="pq-input py-1.5" value={due} onChange={(e) => setDue(e.target.value)} data-testid="inv-due" /></label>
            <div className="text-[11px] text-slate-500 self-end" data-testid="inv-gst-hint">
              {t.tax > 0 ? (t.intra ? `Intra-state → CGST + SGST (${custState || "same as registered state"})` : `Inter-state → IGST${custState ? ` (${custState})` : ""}`) : "No GST on this invoice"}
            </div>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 text-[13px] flex justify-end gap-5 flex-wrap" data-testid="inv-totals">
            <span className="text-slate-500">Taxable <b className="text-slate-800">{m(t.taxable)}</b></span>
            {t.intra ? (
              <>
                <span className="text-slate-500">CGST <b className="text-slate-800">{m(t.cgst)}</b></span>
                <span className="text-slate-500">SGST <b className="text-slate-800">{m(t.sgst)}</b></span>
              </>
            ) : (
              <span className="text-slate-500">IGST <b className="text-slate-800">{m(t.igst)}</b></span>
            )}
            <span className="text-slate-500">Total <b className="metric text-teal-700 text-base" data-testid="inv-total">{m(t.total)}</b></span>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={save} disabled={saving} data-testid="inv-save">{saving ? <Spinner className="w-4 h-4" /> : "Create invoice"}</Btn>
        </div>
      </Card>
    </div>
  );
}

function GstMaster() {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [nr, setNr] = useState({ name: "", rate: "", hsn_sac: "" });
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/finance/gst").then((r) => { setD(r.data); setF({ ...r.data.settings }); }), []);
  useEffect(() => { load(); }, [load]);
  if (!d || !f) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const saveSettings = async () => {
    setSaving(true);
    try { await api.put("/finance/gst/settings", f); toast.success("GST settings saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };
  const addRate = async () => {
    if (nr.rate === "" || Number(nr.rate) < 0) return toast.error("Enter a valid rate %");
    try { await api.post("/finance/gst/rates", { ...nr, rate: Number(nr.rate) }); toast.success("Rate added"); setNr({ name: "", rate: "", hsn_sac: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleRate = async (r) => { await api.put(`/finance/gst/rates/${r.id}`, { active: !r.active }); load(); };
  const delRate = async (r) => { if (!window.confirm(`Delete ${r.name}?`)) return; await api.delete(`/finance/gst/rates/${r.id}`); toast.success("Rate deleted"); load(); };
  return (
    <div data-testid="finance-gst">
      <Card className="mb-5" data-testid="gst-settings">
        <CardHead title="GST Registration & Invoice Settings" action={<Btn size="sm" variant="primary" onClick={saveSettings} disabled={saving} data-testid="gstm-save">{saving ? <Spinner className="w-3.5 h-3.5" /> : "Save settings"}</Btn>} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-5">
          <label className="block"><span className="pq-label">Legal entity name</span><input className="pq-input py-1.5 w-full" value={f.legal_name || ""} onChange={(e) => setF({ ...f, legal_name: e.target.value })} data-testid="gstm-legal" /></label>
          <label className="block"><span className="pq-label">GSTIN</span><input className="pq-input py-1.5 w-full" placeholder="e.g. 29ABCDE1234F1Z5" value={f.gstin || ""} onChange={(e) => setF({ ...f, gstin: e.target.value })} data-testid="gstm-gstin" /></label>
          <label className="block"><span className="pq-label">Registered state</span><StateSelect value={f.registered_state || ""} onChange={(v) => setF({ ...f, registered_state: v })} testid="gstm-state" className="pq-input py-1.5 w-full" /></label>
          <label className="block sm:col-span-2"><span className="pq-label">Business address</span><input className="pq-input py-1.5 w-full" value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="gstm-address" /></label>
          <label className="block"><span className="pq-label">Default SAC (services)</span><input className="pq-input py-1.5 w-full" value={f.sac || ""} onChange={(e) => setF({ ...f, sac: e.target.value })} data-testid="gstm-sac" /></label>
          <label className="block"><span className="pq-label">Invoice prefix</span><input className="pq-input py-1.5 w-full" value={f.invoice_prefix || ""} onChange={(e) => setF({ ...f, invoice_prefix: e.target.value })} data-testid="gstm-prefix" /></label>
          <label className="block"><span className="pq-label">Default GST rate</span>
            <select className="pq-input py-1.5 w-full" value={f.default_tax_rate} onChange={(e) => setF({ ...f, default_tax_rate: Number(e.target.value) })} data-testid="gstm-defrate">
              {d.rates.filter((r) => r.active).map((r) => <option key={r.id} value={r.rate}>{r.name}</option>)}
            </select>
          </label>
          <label className="block"><span className="pq-label">Default tax treatment</span>
            <select className="pq-input py-1.5 w-full" value={f.default_tax_mode} onChange={(e) => setF({ ...f, default_tax_mode: e.target.value })} data-testid="gstm-defmode">
              <option value="exclusive">Tax exclusive</option>
              <option value="inclusive">Tax inclusive</option>
            </select>
          </label>
        </div>
      </Card>
      <Card data-testid="gst-rates">
        <CardHead title="Tax Rate Master (HSN/SAC)" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-5 py-4 border-b border-slate-100 items-end">
          <input className="pq-input py-1.5" placeholder="Rate name (e.g. GST 18%)" value={nr.name} onChange={(e) => setNr({ ...nr, name: e.target.value })} data-testid="rate-add-name" />
          <input type="number" className="pq-input py-1.5 metric" placeholder="Rate %" value={nr.rate} onChange={(e) => setNr({ ...nr, rate: e.target.value })} data-testid="rate-add-pct" />
          <input className="pq-input py-1.5" placeholder="HSN/SAC (optional)" value={nr.hsn_sac} onChange={(e) => setNr({ ...nr, hsn_sac: e.target.value })} data-testid="rate-add-hsn" />
          <Btn variant="primary" onClick={addRate} data-testid="rate-add-btn"><Plus size={14} /> Add rate</Btn>
        </div>
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{["Name", "Rate", "HSN/SAC", "Status", ""].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
          <tbody>
            {d.rates.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 last:border-0 text-[13px]" data-testid={`rate-row-${r.id}`}>
                <td className="px-4 py-2.5 font-semibold">{r.name}</td>
                <td className="px-4 py-2.5 metric">{r.rate}%</td>
                <td className="px-4 py-2.5 text-slate-500">{r.hsn_sac || "—"}</td>
                <td className="px-4 py-2.5"><button onClick={() => toggleRate(r)} data-testid={`rate-toggle-${r.id}`}><Badge tone={r.active ? "emerald" : "slate"}>{r.active ? "Active" : "Inactive"}</Badge></button></td>
                <td className="px-4 py-2.5 text-right"><button onClick={() => delRate(r)} className="text-rose-500 hover:text-rose-700" data-testid={`rate-del-${r.id}`}><Trash2 size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>
    </div>
  );
}

function Notes() {
  const [d, setD] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [f, setF] = useState({ kind: "Credit", invoice_id: "", taxable: "", gst_rate: "", reason: "", date: new Date().toISOString().slice(0, 10) });
  const load = useCallback(() => {
    api.get("/finance/notes").then((r) => setD(r.data));
    api.get("/finance/invoices").then((r) => setInvoices(r.data.invoices));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  const pickInvoice = (id) => {
    const inv = invoices.find((x) => x.id === id);
    setF({ ...f, invoice_id: id, taxable: inv ? inv.subtotal : f.taxable, gst_rate: inv ? inv.tax_pct : f.gst_rate });
  };
  const add = async () => {
    if (!(Number(f.taxable) > 0)) return toast.error("Enter a positive taxable value");
    try {
      await api.post("/finance/notes", { ...f, taxable: Number(f.taxable), gst_rate: f.gst_rate === "" ? undefined : Number(f.gst_rate) });
      toast.success(`${f.kind} note created`); setF({ ...f, invoice_id: "", taxable: "", gst_rate: "", reason: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (n) => { if (!window.confirm(`Delete ${n.note_no}?`)) return; await api.delete(`/finance/notes/${n.id}`); toast.success("Deleted"); load(); };
  return (
    <Card data-testid="finance-notes">
      <CardHead title="Credit / Debit Notes" />
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 px-5 py-4 border-b border-slate-100 items-end">
        <select className="pq-input py-1.5" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="note-kind">{["Credit", "Debit"].map((k) => <option key={k}>{k}</option>)}</select>
        <select className="pq-input py-1.5" value={f.invoice_id} onChange={(e) => pickInvoice(e.target.value)} data-testid="note-invoice">
          <option value="">Link invoice (optional)</option>
          {invoices.map((i) => <option key={i.id} value={i.id}>{i.invoice_no} · {i.client_name}</option>)}
        </select>
        <input type="number" className="pq-input py-1.5 metric" placeholder="Taxable value" value={f.taxable} onChange={(e) => setF({ ...f, taxable: e.target.value })} data-testid="note-taxable" />
        <input type="number" className="pq-input py-1.5 metric" placeholder="GST %" value={f.gst_rate} onChange={(e) => setF({ ...f, gst_rate: e.target.value })} data-testid="note-rate" />
        <input className="pq-input py-1.5" placeholder="Reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="note-reason" />
        <Btn variant="primary" onClick={add} data-testid="note-add"><Plus size={14} /> Add note</Btn>
      </div>
      <div className="overflow-x-auto"><table className="w-full">
        <thead><tr>{["Note", "Type", "Invoice", "Client", "Date", "Taxable", "GST", "Total", ""].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
        <tbody>
          {d.notes.length === 0 ? <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-slate-500">No credit/debit notes yet.</td></tr> :
            d.notes.map((n) => (
              <tr key={n.id} className="border-b border-slate-100 last:border-0 text-[13px]" data-testid={`note-row-${n.id}`}>
                <td className="px-4 py-2.5 font-semibold">{n.note_no}</td>
                <td className="px-4 py-2.5"><Badge tone={n.kind === "Credit" ? "emerald" : "amber"}>{n.kind}</Badge></td>
                <td className="px-4 py-2.5">{n.invoice_no || "—"}</td><td className="px-4 py-2.5">{n.client_name || "—"}</td>
                <td className="px-4 py-2.5 text-slate-500">{n.date}</td>
                <td className="px-4 py-2.5 metric">{m(n.taxable)}</td>
                <td className="px-4 py-2.5 metric">{m((n.cgst || 0) + (n.sgst || 0) + (n.igst || 0))}</td>
                <td className="px-4 py-2.5 metric font-semibold">{m(n.total)}</td>
                <td className="px-4 py-2.5 text-right"><button onClick={() => del(n)} className="text-rose-500 hover:text-rose-700" data-testid={`note-del-${n.id}`}><Trash2 size={15} /></button></td>
              </tr>
            ))}
        </tbody>
      </table></div>
    </Card>
  );
}

function GstReports() {
  const [month, setMonth] = useState("");
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/finance/gst/report${month ? `?month=${month}` : ""}`).then((r) => setD(r.data)), [month]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner className="w-6 h-6 text-teal-600 mx-auto my-16" />;
  const m = (n) => money(n, d.currency);
  const TaxTable = ({ title, rows, testid }) => (
    <Card className="mb-5" data-testid={testid}>
      <CardHead title={title} />
      <div className="overflow-x-auto"><table className="w-full">
        <thead><tr>{["GST rate", "Taxable value", "CGST", "SGST", "IGST", "Total tax"].map((h) => <th key={h} className="pq-label px-4 py-2.5 text-left border-b border-slate-100 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">No data for this period.</td></tr> :
            rows.map((r) => (
              <tr key={r.rate} className="border-b border-slate-100 last:border-0 text-[13px]" data-testid={`${testid}-row-${r.rate}`}>
                <td className="px-4 py-2.5 font-semibold">{r.rate}%</td>
                <td className="px-4 py-2.5 metric">{m(r.taxable)}</td><td className="px-4 py-2.5 metric">{m(r.cgst)}</td>
                <td className="px-4 py-2.5 metric">{m(r.sgst)}</td><td className="px-4 py-2.5 metric">{m(r.igst)}</td>
                <td className="px-4 py-2.5 metric font-semibold">{m(r.tax)}</td>
              </tr>
            ))}
        </tbody>
      </table></div>
    </Card>
  );
  return (
    <div data-testid="finance-gst-reports">
      <div className="flex items-end gap-3 mb-4 flex-wrap">
        <label className="block"><span className="pq-label">Period (blank = all time)</span><input type="month" className="pq-input py-1.5" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="gst-report-month" /></label>
        <div className="text-[12px] text-slate-500 pb-1.5" data-testid="gst-report-entity">
          {d.settings.gstin ? <>GSTIN <b className="text-slate-700">{d.settings.gstin}</b> · {d.settings.registered_state || "state not set"}</> : "Set GSTIN & registered state in GST Master"}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KPI label={`B2B supplies · ${d.b2b.count} invoices`} value={m(d.b2b.taxable)} testid="kpi-b2b" />
        <KPI label={`B2C supplies · ${d.b2c.count} invoices`} value={m(d.b2c.taxable)} testid="kpi-b2c" />
        <KPI label="Output tax (net of notes)" value={m(d.gstr3b.output_tax)} tone="text-amber-600" testid="kpi-output-tax" />
        <KPI label="Input tax credit" value={m(d.gstr3b.itc)} tone="text-teal-700" testid="kpi-itc" />
      </div>
      <Card className="mb-5" data-testid="gstr3b-card">
        <CardHead title="GSTR-3B style summary" />
        <div className="grid grid-cols-3 gap-3 p-5 text-center">
          <div className="bg-slate-50 rounded-xl p-4"><div className="metric text-xl font-extrabold text-amber-600" data-testid="g3b-output">{m(d.gstr3b.output_tax)}</div><div className="text-[11px] text-slate-500 mt-1">Outward tax liability{d.note_adj.credit_tax || d.note_adj.debit_tax ? ` (Cr -${m(d.note_adj.credit_tax)} / Dr +${m(d.note_adj.debit_tax)})` : ""}</div></div>
          <div className="bg-slate-50 rounded-xl p-4"><div className="metric text-xl font-extrabold text-teal-700" data-testid="g3b-itc">{m(d.gstr3b.itc)}</div><div className="text-[11px] text-slate-500 mt-1">Input tax credit (eligible expenses)</div></div>
          <div className="bg-slate-50 rounded-xl p-4"><div className={`metric text-xl font-extrabold ${d.gstr3b.net_payable >= 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="g3b-net">{m(d.gstr3b.net_payable)}</div><div className="text-[11px] text-slate-500 mt-1">{d.gstr3b.net_payable >= 0 ? "Net GST payable" : "Net ITC carried forward"}</div></div>
        </div>
      </Card>
      <TaxTable title="GSTR-1 style · outward supplies by rate (incl. Cr/Dr note adjustments)" rows={d.outward} testid="gstr1-table" />
      <TaxTable title="Input tax credit by rate (ITC-eligible expenses)" rows={d.itc} testid="itc-table" />
    </div>
  );
}
